import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Input } from '@nextui-org/react';
import { KeyRound, Mail, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ForgotPasswordPage: React.FC = () => {
  const { requestPasswordReset } = useAuth();

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email.trim())) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }

    setIsSubmitting(true);
    try {
      await requestPasswordReset(email.trim().toLowerCase());
      setSuccess(true);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Anfrage fehlgeschlagen. Bitte versuche es später erneut.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Passwort vergessen | DartCloud</title>
      </Helmet>

      <div className="min-h-[calc(100vh-6rem)] flex items-center justify-center px-4 py-12 bg-black">
        <Card className="w-full max-w-md bg-zinc-900/80 border border-zinc-800 backdrop-blur">
          <CardHeader className="flex flex-col items-start gap-2 px-6 pt-6 pb-2">
            <div className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#3fced1]/10 text-[#3fced1]">
                <KeyRound size={20} />
              </span>
              <h1 className="text-2xl font-bold text-white">Passwort zurücksetzen</h1>
            </div>
            <p className="text-sm text-gray-400">
              Gib deine E-Mail-Adresse ein. Wir schicken dir einen Link zum Zurücksetzen.
            </p>
          </CardHeader>

          <CardBody className="px-6 pb-6 pt-2">
            {success ? (
              <div className="flex flex-col items-start gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-200">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={18} />
                  <span className="font-medium">E-Mail versendet</span>
                </div>
                <p>
                  Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir dir
                  Anweisungen zum Zurücksetzen deines Passworts geschickt.
                </p>
                <RouterLink
                  to="/login"
                  className="text-[#3fced1] hover:text-[#35b5b8] font-medium"
                >
                  Zurück zum Login
                </RouterLink>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                <Input
                  type="email"
                  label="E-Mail"
                  placeholder="deine@email.de"
                  value={email}
                  onValueChange={setEmail}
                  isRequired
                  autoComplete="email"
                  startContent={<Mail size={18} className="text-gray-400" />}
                  variant="bordered"
                  classNames={{
                    inputWrapper:
                      'bg-zinc-950 border-zinc-700 data-[hover=true]:border-zinc-600 group-data-[focus=true]:border-[#3fced1]',
                    input: 'text-white placeholder:text-gray-500',
                    label: 'text-gray-300',
                  }}
                />

                {error && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                  size="lg"
                >
                  {isSubmitting ? 'Wird gesendet …' : 'Link anfordern'}
                </Button>

                <p className="text-center text-sm text-gray-400">
                  <RouterLink
                    to="/login"
                    className="text-[#3fced1] hover:text-[#35b5b8] font-medium"
                  >
                    Zurück zum Login
                  </RouterLink>
                </p>
              </form>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
